import { Flex, Grid } from 'components/box';
import {
  CustomizableOptionValueInterface,
  ProductGalleryItemInterface,
  ProductGalleryItemTypeEnum,
  ProductInterface,
} from 'types/types';
import SavingsBadge from 'components/savings-badge';
import { css } from '@emotion/react';
import RenderControl from 'components/_shared/widgets/render-control';
import Button from 'components/button';
import DesktopCarousel from './desktop';
import MobileCarousel from './mobile';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { splitArray } from '../../../../../util/arrays';
import mem from 'mem';
import Loading from 'components/loading';

const moveVideosToIndex = (
  allMedia: ProductGalleryItemInterface[],
  idx: number
) => {
  const [videos, images] = splitArray(
    allMedia,
    media => media.type === ProductGalleryItemTypeEnum.ExternalVideo
  );
  return [...images.slice(0, idx), ...videos, ...images.slice(idx)];
};

const isolateLabel = mem((label: string) => {
  if (label.includes(' - ')) {
    return label.substring(0, label.indexOf(' - '));
  }
  if (label.includes(' (')) {
    return label.substring(0, label.indexOf(' ('));
  }
  return label;
});

/**
 * Format for label matching:
 * exact matching label - extra info (price)
 * Example:
 * Green - XL (R700)
 * We then only match the strings before the '-' or '(' on both the image and custom option.
 */
const areMatchingLabels = (labelA: string | null, labelB: string | null) => {
  if (!labelA || !labelB) {
    return false;
  }

  return isolateLabel(labelA) === isolateLabel(labelB);
};

interface CarouselProps {
  product: ProductInterface;
  selectedOption?: CustomizableOptionValueInterface;
}

const Carousel = ({ product, selectedOption }: CarouselProps) => {
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [shouldCensorMedia, setShouldCensorMedia] = useState(true);
  const [shouldSelectOptionImage, setShouldSelectOptionImage] = useState(false);

  const [censored, uncensored] = splitArray(
    product.gallery || [],
    media => !!media.file.isCensored
  );

  const censoredMedia = useMemo(() => moveVideosToIndex(censored, 1), [
    censored,
  ]);
  const uncensoredMedia = useMemo(() => moveVideosToIndex(uncensored, 1), [
    uncensored,
  ]);

  const hasCensoredMedia = censoredMedia.length > 0;

  const allMedia = useMemo(
    () =>
      censoredMedia.length > 0 && shouldCensorMedia
        ? censoredMedia
        : uncensoredMedia,
    [shouldCensorMedia, censoredMedia, uncensoredMedia]
  );

  const showSavings =
    !product.hasPriceRange ||
    !selectedOption ||
    selectedOption.price.fixed.value === product.price.value;

  const selectOptionImage = useCallback(() => {
    if (allMedia && selectedOption) {
      const resultIdx = allMedia.findIndex(
        media =>
          media.label && areMatchingLabels(media.label, selectedOption.label)
      );
      if (resultIdx !== -1) {
        setActiveMediaIndex(resultIdx);
      }
    }
  }, [allMedia, selectedOption]);

  /**
   * State indirection necessary to block the image selection happening again on re-renders.
   *
   * This is necessary because allMedia is being re-created on re-render.
   * Causing whatever function we use for the image selection to re-run.
   * Locking us to the selected option image.
   */
  useEffect(() => {
    !!selectedOption && setShouldSelectOptionImage(true);
  }, [selectedOption]);

  /**
   * Jump to selected options image.
   */
  useEffect(() => {
    if (shouldSelectOptionImage) {
      setShouldSelectOptionImage(false);
      selectOptionImage();
    }
  }, [shouldSelectOptionImage, selectOptionImage]);

  return (
    <div
      css={css`
        user-select: none;
        position: relative;
      `}
    >
      {showSavings && (
        <SavingsBadge
          borderRadiusBottomRight={[false, false, true]}
          borderRadiusBottomLeft={[true, true, true]}
          borderRadiusTopLeft={[true, true, false]}
          saving={product.saving}
          isAccented={[true, true, true]}
          mr={[-3, 0, '3.5%']}
        />
      )}
      <Grid
        gridTemplateAreas={"'main' 'thumbs'"}
        gridTemplateColumns={['auto', '1fr']}
        gridTemplateRows="auto"
        gridColumnGap={0}
      >
        <RenderControl type="server" screen="tabletUp">
          <DesktopCarousel
            media={allMedia}
            isSoldOut={product.isSoldOut}
            activeMediaIndex={activeMediaIndex}
            setActiveMediaIndex={setActiveMediaIndex}
            hideThumbnail={product.permanentShop?.id === 'gift-vouchers'}
            isPromotionalDeal={product.isPromotionalDeal}
            price={product.price.formattedValue}
          />
        </RenderControl>

        <RenderControl type="client" screen="mobileOnly">
          <MobileCarousel
            mobileBulletHeight="-20px"
            media={allMedia}
            isSoldOut={product.isSoldOut}
            activeMediaIndex={activeMediaIndex}
            setActiveMediaIndex={setActiveMediaIndex}
            hideThumbnail={product.permanentShop?.id === 'gift-vouchers'}
            isPromotionalDeal={product.isPromotionalDeal}
            price={product.price.formattedValue}
          />
        </RenderControl>

        {hasCensoredMedia && (
          <Flex
            justifyContent={['center', 'flex-start']}
            // NOTE: if we add the thumbnails back on mobile this will need to be changed to 3/1 for all screens
            gridArea={['2/1', '3/1']}
            py={[3, 0]}
          >
            <Button
              variant="outlined"
              size="small"
              shouldFocus={false}
              onClick={() =>
                setShouldCensorMedia(shouldCensorMedia => !shouldCensorMedia)
              }
            >
              show {shouldCensorMedia ? 'uncensored' : 'censored'} images
            </Button>
          </Flex>
        )}
      </Grid>
    </div>
  );
};

const Wrapper = ({ product, ...restProps }: CarouselProps) => {
  /**
   * We need to bail out if the gallery is empty as designs don't cater for it.
   */
  if (!product.gallery || product.gallery.length === 0) {
    // TODO: FE-600 get some kind of icon or graphic in here
    return (
      <Loading isLoading>
        <div
          css={css`
            height: 200px;
          `}
        />
      </Loading>
    );
  }

  return <Carousel product={product} {...restProps} />;
};

export default Wrapper;
